import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode, Mousewheel, Scrollbar } from "swiper/modules";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { core_stack } from "../data/coreStack";
import DarkRedButton from "./DarkRedButton";
import "swiper/css";
import "swiper/css/free-mode";

// The three companies in the nav's own "Our Companies" dropdown — plain
// text links, same treatment as Fynn.io below. Images live on the
// discipline cards in the slider above, not here.
const OUR_COMPANIES = [
  { label: "Galerie Capital", to: "/capital" },
  { label: "Galerie Development", to: "/development" },
  { label: "Galerie Management", to: "/management" },
];

const FYNN_LINK = { label: "Fynn.io", to: "https://fynn.io", external: true };

// Non-interactive discipline tiles (no links, no hover state) — the point
// is the copy, not a click target. Same free-scroll/drag/mousewheel feel as
// the other project sliders, with arrows that snap by a full row.
export default function CoreStackSlider({
  title = "Every Discipline. One Team.",
  subhead = "A Unified Front",
  lead = "As owners, developers, and operators, our vertically-integrated family of disciplines establishes a collaborative union that embodies bold strategy, visionary foresight, and class-redefining leadership.",
  class_name = "",
}) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const scrollbarRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const updateEdges = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <div className={`core-stack-section ${class_name}`}>
      <div className="core-stack-header">
        <div className="core-stack-header-text">
          <p className="health-header-title">{subhead}</p>
          <h2 className="core-stack-title">{title}</h2>
          <p className="core-stack-lead">{lead}</p>
        </div>

        <div className="core-stack-arrows">
          <div
            ref={prevRef}
            className={`core-stack-prev-arrow ${isBeginning ? "disabled" : ""}`}
            data-testid="core-stack-prev-btn"
          >
            <ArrowLeft
              className={
                isBeginning
                  ? "core-stack-arrow-icon-disabled"
                  : "core-stack-arrow-icon"
              }
            />
          </div>

          <div
            ref={nextRef}
            className={`core-stack-next-arrow ${isEnd ? "disabled" : ""}`}
            data-testid="core-stack-next-btn"
          >
            <ArrowRight
              className={
                isEnd ? "core-stack-arrow-icon-disabled" : "core-stack-arrow-icon"
              }
            />
          </div>
        </div>
      </div>

      <div
        ref={scrollbarRef}
        className="slider-progress-track"
        data-testid="core-stack-progress"
      />

      <Swiper
        modules={[Navigation, FreeMode, Mousewheel, Scrollbar]}
        spaceBetween={24}
        slidesPerView={1.2}
        slidesPerGroup={1}
        freeMode={{
          enabled: true,
          sticky: false,
          momentum: true,
        }}
        mousewheel={{
          forceToAxis: true,
        }}
        grabCursor={true}
        breakpoints={{
          550: { slidesPerView: 2.3 },
          900: { slidesPerView: 3.3 },
          1300: { slidesPerView: 5, slidesPerGroup: 5 },
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        // Scrollbar's drag-thumb size/position is computed from the real
        // visible-vs-total slide ratio and recalculates on its own on
        // resize/breakpoint changes — see .slider-progress-track in
        // _components.scss for the red-highlight styling. draggable stays
        // false: it's a read-only indicator, and turning it on wires up
        // document-level pointermove/pointerup listeners (Swiper's
        // onDragMove/onDragEnd) that can throw once the React wrapper's own
        // prop-diffing re-inits the scrollbar module across re-renders.
        scrollbar={{
          el: scrollbarRef.current,
          draggable: false,
          hide: false,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
          swiper.params.scrollbar.el = scrollbarRef.current;
        }}
        onSwiper={updateEdges}
        onSlideChange={updateEdges}
        className="core-stack-swiper"
      >
        {core_stack.map((item) => (
          <SwiperSlide key={item.id} className="core-stack-slide">
            <div className="core-stack-card">
              {item.image && (
                <img
                  className="core-stack-card-img"
                  src={item.image}
                  alt={item.title}
                />
              )}
              <div className="core-stack-card-body">
                <h3 className="core-stack-card-title">{item.title}</h3>
                <p className="core-stack-card-text">{item.body}</p>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="core-stack-team-row">
        <DarkRedButton
          class_name="core-stack-team-btn"
          btnText="Meet Our Team"
          btnLink="/team"
          data_test_id="core-stack-team-btn"
        />

        <div className="core-stack-team-line" aria-hidden="true" />

        <div className="core-stack-companies">
          <p className="core-stack-companies-label">Explore Our Companies</p>

          <div className="core-stack-companies-links">
            {OUR_COMPANIES.map((company) => (
              <Link
                key={company.to}
                to={company.to}
                className="core-stack-link"
                data-testid={`core-stack-company-${company.to.replace("/", "")}`}
              >
                <span>{company.label}</span>
                <ArrowUpRight size={18} />
              </Link>
            ))}

            <a
              href={FYNN_LINK.to}
              target="_blank"
              rel="noreferrer"
              className="core-stack-link"
              data-testid="core-stack-link-fynn-io"
            >
              <span>{FYNN_LINK.label}</span>
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
